import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Snackbar,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { ArrowDropDown, ArrowDropUp, CloseOutlined, HistoryOutlined } from '@mui/icons-material';
import { Alert } from '@mui/lab';
import { COLORS } from '../../../utils/Application_Constants';
import { formatErrorMessage } from '../../../Shared/Utils/ErrorMessage';
import useGetProcessedTimesheetHistory from '../../hooks/useGetProcessedTimesheetHistory';
import { mapProcessedTimesheetHistory } from '../../Utils/processedTimesheetHistory';

const EMPTY_ARRAY = [];

const styles = (theme) => ({
    DrawerPaper: {
        padding: '16px 40px 40px 40px',
        maxWidth: '400px',
        [theme.breakpoints.up('md')]: {
            width: '400px',
        },
    },
    historyHeader: {
        color: COLORS.LT_MIDNIGHT50,
        padding: '4px !important',
    },
    historyEvents: {
        paddingBottom: '64px',
    },
    historyEventDetail: {
        paddingLeft: '16px',
    },
    detailLabel: {
        fontWeight: '600',
        display: 'flex',
        cursor: 'pointer',
    },
    progressRoot: {
        margin: '40px auto',
    },
});

const ProcessedTimesheetHistory = (props) => {
    const {
        classes,
        processedTimesheetId,
        locations = EMPTY_ARRAY,
        availableRates = EMPTY_ARRAY,
        entries = EMPTY_ARRAY,
    } = props;
    const [open, setOpen] = useState(false);
    const [eventDetailOpen, setEventDetailOpen] = useState();
    const [alert, setAlert] = useState();

    const { data, error, isError, isFetching, refetch } = useGetProcessedTimesheetHistory(
        { processedTimesheetId },
        { enabled: open && !!processedTimesheetId }
    );
    const timesheetHistory = useMemo(
        () => mapProcessedTimesheetHistory(data, { locations, availableRates, entries }),
        [data, locations, availableRates, entries]
    );
    const errorMessage = isError && !isFetching ? formatErrorMessage(error) : null;

    useEffect(() => {
        if (!open || isFetching) {
            return;
        }
        if (isError) {
            setAlert({
                message: formatErrorMessage(error),
                type: 'error',
            });
        }
    }, [open, isFetching, isError, error]);

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert(null);
    };

    const handleDrawerOpen = () => {
        if (isFetching) {
            return;
        }
        setEventDetailOpen(null);
        setAlert(null);
        if (open) {
            refetch();
            return;
        }
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
        setEventDetailOpen(null);
        setAlert(null);
    };

    const handleEventDetail = (index) => {
        const eventDetails = index === eventDetailOpen ? null : index;
        setEventDetailOpen(eventDetails);
    };

    const renderAlert = () => (
        <Snackbar
            open={Boolean(alert)}
            autoHideDuration={6000}
            onClose={handleAlertClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert onClose={handleAlertClose} severity={alert?.type}>
                {alert?.message}
            </Alert>
        </Snackbar>
    );

    const renderHistoryEvent = (event, index) => (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <Typography variant="body2">
                    <b>
                        {event.headingOne}
                        {event.headingOneRemovedValue ? (
                            <span style={{ textDecoration: 'line-through' }}>
                                {event.headingOneRemovedValue}
                            </span>
                        ) : null}
                    </b>
                </Typography>
            </Grid>
            <Grid item>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {event.headingTwo}
                </Typography>
            </Grid>
            {event.details?.length > 0 && (
                <Grid item>
                    <Typography
                        onClick={() => handleEventDetail(index)}
                        variant="caption"
                        className={classes.detailLabel}
                        gutterBottom
                    >
                        Details
                        {index !== eventDetailOpen ? (
                            <ArrowDropDown fontSize="small" />
                        ) : (
                            <ArrowDropUp fontSize="small" />
                        )}
                    </Typography>
                    <Collapse in={index === eventDetailOpen} timeout="auto" unmountOnExit>
                        <Grid container spacing={2}>
                            {event.details.map((detail, detailIndex) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Grid item xs={12} key={detailIndex}>
                                    <Typography
                                        variant="body2"
                                        className={classes.historyEventDetail}
                                        sx={
                                            detail.changeKind === 'removed'
                                                ? { textDecoration: 'line-through' }
                                                : undefined
                                        }
                                    >
                                        {detail.detail}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Collapse>
                </Grid>
            )}
        </Grid>
    );

    const renderHistoryEvents = () => (
        <>
            {isFetching && (
                <CircularProgress classes={{ root: classes.progressRoot }} color="primary" />
            )}
            {!isFetching && !errorMessage && timesheetHistory.length > 0 && (
                <>
                    {timesheetHistory.map((event, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={`${event.headingTwo}-${index}`}>
                            <Grid item className={classes.historyEvent}>
                                {renderHistoryEvent(event, index)}
                            </Grid>
                            {index !== timesheetHistory.length - 1 && (
                                <Grid item>
                                    <Divider />
                                </Grid>
                            )}
                        </React.Fragment>
                    ))}
                </>
            )}
            {!isFetching && errorMessage && (
                <Grid item>
                    <Typography variant="body2">{errorMessage}</Typography>
                </Grid>
            )}
            {!isFetching && !errorMessage && timesheetHistory.length === 0 && (
                <Grid item>
                    <Typography variant="body2">No history available.</Typography>
                </Grid>
            )}
        </>
    );

    const renderHistoryDrawer = () => (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleDrawerClose}
            classes={{ paper: classes.DrawerPaper }}
            sx={() => ({
                zIndex: 30000,
            })}
        >
            <Grid container direction="column" spacing={2} className={classes.historyEvents}>
                <Grid item className={classes.historyHeader}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Grid container spacing={1} alignItems="center">
                                <HistoryOutlined fontSize="small" />
                                <Grid item>
                                    <Typography variant="overline" display="block">
                                        <b>Processed Timesheet History</b>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={handleDrawerClose} size="large">
                                <CloseOutlined />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
                <Divider />
                {renderHistoryEvents()}
            </Grid>
        </Drawer>
    );

    return (
        <>
            <Button
                onClick={handleDrawerOpen}
                startIcon={<HistoryOutlined />}
                color="default"
                disabled={isFetching}
            >
                Processed Timesheet History
            </Button>
            {renderHistoryDrawer()}
            {renderAlert()}
        </>
    );
};

export default withStyles(styles)(ProcessedTimesheetHistory);
